import { checkIsTabHidden } from 'app/pages/system/autoLockRules/AutoLockRules.utils';
import _ from 'lodash';

import { assetsMap, incomeMap, typeOfCreditMap } from 'constants/options';
import { formatNumberToCurrencyString } from 'utils/number';

import {
  ALL_BORROWERS_PATH,
  FINANCIAL_INFO_VIEWS_PATHS,
  NEW_RECORD_DATA,
} from '../FinancialInfo.constants';
import { filterClosedLiabilities } from '../FinancialInfo.utils';

import { DEFAULT_RECORD_NOUN, RECORD_NOUN_BY_TABLE_PATH } from './UpdateTableModal.constants';

export const getRecordFlags = ({
  deletedRecords,
  deletedInitialRecords,
  newAddedInitialRecords,
  id,
}) => ({
  isDeletedByCurrentSession: deletedRecords.has(id),
  isDeletedByInit: deletedInitialRecords.has(id),
  isNewAdded: newAddedInitialRecords.has(id),
});

export const getRecordDisplayName = ({ tablePath, record }) => {
  switch (true) {
    case record.id.startsWith(NEW_RECORD_DATA.id):
      return null;

    case tablePath === FINANCIAL_INFO_VIEWS_PATHS.INCOME_AND_EMPLOYMENT:
      return record.employerName && !record.endDate
        ? record.employerName
        : incomeMap[record.incomeType]?.name;

    case tablePath === FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES:
      return `${
        record.creditorName ??
        typeOfCreditMap[record.liabilityAccountType ?? record.liabilityExpenseType]?.name
      } | ${formatNumberToCurrencyString(record.unpaidBalance, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;

    case tablePath === FINANCIAL_INFO_VIEWS_PATHS.ASSETS:
      return `${
        record.institution ?? assetsMap[record.assetTypeName]?.name
      } | ${formatNumberToCurrencyString(record.balance)}`;

    case tablePath === FINANCIAL_INFO_VIEWS_PATHS.REO:
      return `${record.address}`;

    default:
      return 'Unknown';
  }
};

export const hasAnyValidationError = validationErrorSummary => !_.isEmpty(validationErrorSummary);

export const getInvalidBorrowerIds = validationErrorSummary =>
  new Set(Object.values(validationErrorSummary).flatMap(({ ownerIds }) => ownerIds));

export const getKeptClosedRecordIds = ({
  validationErrorSummary,
  recordId,
  isRevealed,
  previouslyInvalidRecordIds,
}) => {
  const keptRecordIds = new Set(Object.keys(validationErrorSummary));

  if (recordId && (isRevealed || previouslyInvalidRecordIds.has(recordId))) {
    keptRecordIds.add(recordId);
  }

  return keptRecordIds;
};

export const getVisibleTableData = ({
  tablePath,
  records,
  newRecords,
  isClosedHidden,
  keptRecordIds,
}) => {
  if (!isClosedHidden || tablePath !== FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES) {
    return [...records, ...newRecords];
  }

  const openRecordIds = new Set(filterClosedLiabilities(records).map(({ id }) => id));

  return [
    ...records.filter(({ id }) => openRecordIds.has(id) || keptRecordIds.has(id)),
    ...newRecords,
  ];
};

export const shouldRedirectToClosest = ({
  data,
  recordId,
  isCorrectPath,
  isAddNewRecordInitiated,
}) =>
  isCorrectPath &&
  !isAddNewRecordInitiated &&
  !!data.length &&
  !(recordId && data.some(({ id }) => id === recordId));

export const getFirstInvalidRecordId = ({ orderedRecordLists, invalidRecordIds }) => {
  const invalidIds = new Set(invalidRecordIds);

  const firstListedId = orderedRecordLists
    .map(records => records.find(({ id }) => invalidIds.has(id))?.id)
    .find(Boolean);

  return firstListedId ?? invalidRecordIds[0] ?? null;
};

const hasRecord = (records, recordId) => records.some(({ id }) => id === recordId);

export const getSaveBlockedTarget = ({
  targetId,
  borrowerId,
  currentData,
  allVisibleRecords,
  allRecords,
  isClosedHidden,
  isAllBorrowersTabVisible,
  hiddenPageIds,
  hiddenFieldIds,
  isNestedEntityEditActive,
}) => {
  if (isNestedEntityEditActive || checkIsTabHidden(targetId, hiddenPageIds, hiddenFieldIds)) {
    return null;
  }

  if (hasRecord(currentData, targetId)) {
    return { borrowerId, reveal: false };
  }

  if (hasRecord(allVisibleRecords, targetId)) {
    return isAllBorrowersTabVisible ? { borrowerId: ALL_BORROWERS_PATH, reveal: false } : null;
  }

  const closedRecord = isClosedHidden ? allRecords.find(({ id }) => id === targetId) : null;

  if (!closedRecord) {
    return null;
  }

  if ((closedRecord.owners ?? []).includes(borrowerId)) {
    return { borrowerId, reveal: true };
  }

  return isAllBorrowersTabVisible ? { borrowerId: ALL_BORROWERS_PATH, reveal: true } : null;
};

const getFieldErrorText = ({ label, message }) => (label ? `${label}: ${message}` : message);

export const getSaveBlockedMessage = ({ tablePath, payload, recordName, isTargetShown }) => {
  const noun = RECORD_NOUN_BY_TABLE_PATH[tablePath] ?? DEFAULT_RECORD_NOUN;
  const displayName = recordName ?? `a new ${noun.singular}`;
  const invalidRecordsCount = _.uniqBy(payload, 'recordId').length;

  if (invalidRecordsCount > 1) {
    const targetHint = isTargetShown ? `Showing ${displayName}.` : `First: ${displayName}.`;

    return `Can't save: ${invalidRecordsCount} ${noun.plural} have missing or invalid fields. ${targetHint}`;
  }

  const [firstError] = payload;

  if (payload.length === 1) {
    return `Can't save: ${displayName} — ${getFieldErrorText(firstError)}`;
  }

  const moreFieldsCount = payload.length - 1;
  const fieldsWord = moreFieldsCount === 1 ? 'field' : 'fields';

  return `Can't save: ${displayName} — ${firstError.label || firstError.message} and ${moreFieldsCount} more ${fieldsWord} need attention.`;
};
